import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function AnalyticsCharts({ primaryData, comparisonData, compareMode }) {
  const [activeChart, setActiveChart] = useState('timeline');

  // Prepare timeline data
  const timelineData = Object?.entries(primaryData?.dailyMetrics || {})?.map(([date, metrics]) => ({
    date: format(new Date(date), 'MMM dd'),
    profileViews: metrics?.profileViews || 0,
    contentUploads: metrics?.contentUploads || 0,
    appDownloads: metrics?.appDownloads || 0,
    pageClicks: metrics?.pageClicks || 0
  }));

  // Prepare role distribution data
  const roleData = Object?.entries(primaryData?.byRole || {})?.map(([role, count]) => ({
    name: role?.charAt(0)?.toUpperCase() + role?.slice(1),
    value: count
  }));

  // Prepare artist type distribution data
  const artistTypeData = Object?.entries(primaryData?.byArtistType || {})?.map(([type, count]) => ({
    name: type?.charAt(0)?.toUpperCase() + type?.slice(1),
    value: count
  }));

  // Prepare activity breakdown data
  const activityData = Object?.entries(primaryData?.activities || {})?.map(([activity, count]) => ({
    name: activity?.replace(/_/g, ' ')?.split(' ')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' '),
    count: count
  }));

  return (
    <div className="space-y-6">
      {/* Chart Type Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-3 overflow-x-auto">
          <button
            onClick={() => setActiveChart('timeline')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeChart === 'timeline' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Timeline Trends
          </button>
          <button
            onClick={() => setActiveChart('roles')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeChart === 'roles' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Role Distribution
          </button>
          <button
            onClick={() => setActiveChart('artistTypes')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeChart === 'artistTypes' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Artist Types
          </button>
          <button
            onClick={() => setActiveChart('activities')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeChart === 'activities' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Activity Breakdown
          </button>
        </div>
      </div>
      {/* Timeline Trends Chart */}
      {activeChart === 'timeline' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Metrics Over Time</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="profileViews" stroke="#3B82F6" strokeWidth={2} name="Profile Views" />
              <Line type="monotone" dataKey="appDownloads" stroke="#10B981" strokeWidth={2} name="App Downloads" />
              <Line type="monotone" dataKey="pageClicks" stroke="#F59E0B" strokeWidth={2} name="Page Clicks" />
              <Line type="monotone" dataKey="contentUploads" stroke="#EF4444" strokeWidth={2} name="Content Uploads" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Role Distribution Chart */}
      {activeChart === 'roles' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Role Distribution</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={roleData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {roleData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col justify-center space-y-3">
              {roleData?.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                    />
                    <span className="font-medium text-gray-900">{item?.name}</span>
                  </div>
                  <span className="text-gray-600 font-semibold">{item?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Artist Type Distribution Chart */}
      {activeChart === 'artistTypes' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Artist Type Distribution</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={artistTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#3B82F6" name="Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Activity Breakdown Chart */}
      {activeChart === 'activities' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Activity Breakdown</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={activityData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#10B981" name="Activity Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}